import { useState, useCallback, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Segmented,
  Select,
  Space,
  List,
  Tag,
} from "antd";
import {
  IndianRupee,
  Users,
  Calendar,
  BarChart3,
  TrendingUp,
  TrendingDown,
  TicketPercent,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import PageLoader from "Components/PageLoader/PageLoader";
import { getVendorOverview } from "services/dashboard.service";
import { useCommunitySelector } from "hooks/useCommunitySelector/useCommunitySelector";
import { renderErrorNotifications } from "helpers/error.helpers";
import useAuthStore from "stores/AuthStore/AuthStore";
import useDiscountCodesConfig from "hooks/useDiscountCodesConfig/useDiscountCodesConfig";

const { Title, Text } = Typography;

const PERIOD_OPTIONS = [
  { label: "7 Days", value: "7d" },
  { label: "30 Days", value: "30d" },
  { label: "90 Days", value: "90d" },
  { label: "12 Months", value: "12m" },
];

const PIE_COLORS = ["#6366f1", "#f97316", "#10b981", "#f43f5e", "#8b5cf6"];

function Analytics() {
  const { isRootUser } = useAuthStore();
  const { enabled: codesEnabled } = useDiscountCodesConfig();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("7d");

  const {
    communityOptions,
    selectedCommunityId,
    setSelectedCommunityId,
    loading: communitiesLoading,
  } = useCommunitySelector({ isRootUser });

  const loadData = useCallback(
    async ({ showLoading = false } = {}) => {
      if (showLoading) setLoading(true);
      const params = { period };
      if (selectedCommunityId) params.community_id = selectedCommunityId;
      const { data: result, errors } = await getVendorOverview(params);
      if (errors?.length > 0) {
        renderErrorNotifications(errors);
        setLoading(false);
        return;
      }
      setData(result);
      setLoading(false);
    },
    [period, selectedCommunityId]
  );

  useEffect(() => {
    loadData({ showLoading: true });
  }, [loadData]);

  if (loading || communitiesLoading)
    return <PageLoader showHero={false} showTabs={false} showCards={false} />;

  const summary = data?.summary ?? {};
  const trends = data?.trends ?? {};

  const eventsData = summary.events ?? {};
  const membersData = summary.members ?? {};
  const ordersData = summary.orders ?? {};

  const revenueGrowthPct = ordersData.revenue_growth_pct;
  const memberGrowthPct = membersData.growth_30d_pct;

  const summaryCards = [
    {
      title: "Gross Revenue",
      value: ordersData.gross_revenue,
      prefix: "₹",
      precision: 2,
      change: revenueGrowthPct,
      icon: <IndianRupee size={18} />,
      color: "#52c41a",
    },
    {
      title: "Total Events",
      value: eventsData.total,
      suffix: `${eventsData.this_month ?? 0} this month`,
      icon: <Calendar size={18} />,
      color: "#722ed1",
    },
    {
      title: "Avg. Ticket Price",
      value: trends.revenue?.avg_ticket_price,
      prefix: "₹",
      precision: 2,
      icon: <BarChart3 size={18} />,
      color: "#fa8c16",
    },
  ];

  if (isRootUser) {
    summaryCards.push({
      title: "Active Members",
      value: membersData.active,
      suffix: "Right now (not affected by period)",
      change: memberGrowthPct,
      icon: <Users size={18} />,
      color: "#1677ff",
    });
  }

  // Shown while codes are on, or when the period has coded orders (e.g. after
  // a rollback). Otherwise the grid stays exactly as before (D45).
  const showCodesCard =
    codesEnabled || Number(ordersData.code_discount_given ?? 0) > 0;
  if (showCodesCard) {
    summaryCards.push({
      title: "Discount Given by Codes",
      value: ordersData.code_discount_given ?? 0,
      prefix: "₹",
      precision: 2,
      suffix: "Not taken off gross revenue",
      icon: <TicketPercent size={18} />,
      // Same colour as the Gross Revenue card (no new colour).
      color: summaryCards[0].color,
    });
  }

  const revenueTrend = (trends.revenue?.trend ?? []).map((t) => ({
    month: t.month,
    events: t.events_revenue ?? 0,
    memberships: t.membership_revenue ?? 0,
  }));

  const byEntityType = trends.revenue?.by_entity_type ?? {};
  const pieData = Object.entries(byEntityType).map(([source, value]) => ({
    source,
    value,
  }));

  const memberTrend = (trends.members?.trend ?? []).map((t) => ({
    month: t.month,
    newMembers: t.new_members ?? 0,
    churned: t.churned ?? 0,
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Header */}
      <Row justify="space-between" align="middle" wrap={false}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Analytics &amp; Reports
          </Title>
          <Text type="secondary">
            Track your performance, revenue, and growth metrics for your
            business.
          </Text>
        </Col>
        <Col>
          <Space wrap>
            {isRootUser && (
              <Select
                allowClear
                placeholder="All Communities"
                options={communityOptions}
                value={selectedCommunityId}
                onChange={setSelectedCommunityId}
                style={{ minWidth: 200 }}
              />
            )}
            <Segmented
              options={PERIOD_OPTIONS}
              value={period}
              onChange={setPeriod}
            />
          </Space>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row gutter={[16, 16]}>
        {summaryCards.map((card) => (
          <Col
            key={card.title}
            xs={24}
            sm={12}
            lg={showCodesCard ? { flex: "1 1 0" } : isRootUser ? 6 : 8}
          >
            <Card size="small" style={{ height: "100%" }}>
              <Row justify="space-between" align="top">
                <Col>
                  <Text
                    type="secondary"
                    style={{
                      fontSize: 12,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    {card.title}
                  </Text>
                </Col>
                <Col>{card.icon}</Col>
              </Row>
              <Statistic
                value={card.value}
                prefix={card.prefix}
                precision={card.precision}
                style={{ marginTop: 8 }}
              />
              <Space size={8} style={{ marginTop: 4 }}>
                {card.suffix && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {card.suffix}
                  </Text>
                )}
                {card.change && (
                  <Text
                    style={{
                      fontSize: 12,
                      color: card.change >= 0 ? "#52c41a" : "#ff4d4f",
                    }}
                  >
                    {card.change >= 0 ? (
                      <TrendingUp size={12} style={{ marginRight: 2 }} />
                    ) : (
                      <TrendingDown size={12} style={{ marginRight: 2 }} />
                    )}
                    {card.change >= 0 ? "+" : ""}
                    {card.change?.toFixed(1)}%
                  </Text>
                )}
              </Space>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Charts Row */}
      <Row gutter={[16, 16]}>
        {/* Revenue Over Time */}
        <Col xs={24} lg={16}>
          <Card
            title={
              <div>
                <Text strong>Revenue Over Time</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Monthly breakdown by source
                </Text>
              </div>
            }
          >
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={revenueTrend}>
                <defs>
                  <linearGradient id="colorEvents" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient
                    id="colorMemberships"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${v}`} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, fontSize: 12 }}
                  formatter={(value) => [`₹${value.toLocaleString()}`, ""]}
                />
                <Area
                  type="monotone"
                  dataKey="events"
                  stroke="#6366f1"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorEvents)"
                  name="Events"
                />
                {isRootUser && (
                  <Area
                    type="monotone"
                    dataKey="memberships"
                    stroke="#f97316"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorMemberships)"
                    name="Memberships"
                  />
                )}
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Revenue by Source */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <div>
                <Text strong>Revenue by Source</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Distribution across channels
                </Text>
              </div>
            }
            style={{ height: "100%" }}
          >
            {pieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={80}
                      paddingAngle={4}
                      dataKey="value"
                      nameKey="source"
                    >
                      {pieData.map((_, index) => (
                        <Cell
                          key={index}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ borderRadius: 12, fontSize: 12 }}
                      formatter={(value) => [`₹${value.toLocaleString()}`, ""]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ marginTop: 8 }}>
                  {pieData.map((item, i) => (
                    <Row
                      key={item.source}
                      justify="space-between"
                      style={{ marginBottom: 6 }}
                    >
                      <Col>
                        <Space size={8}>
                          <div
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              backgroundColor:
                                PIE_COLORS[i % PIE_COLORS.length],
                            }}
                          />
                          <Text type="secondary">{item.source}</Text>
                        </Space>
                      </Col>
                      <Col>
                        <Text strong>₹{item.value?.toLocaleString()}</Text>
                      </Col>
                    </Row>
                  ))}
                </div>
              </>
            ) : (
              <Text type="secondary">No data available.</Text>
            )}
          </Card>
        </Col>
      </Row>

      {/* Bottom Row */}
      <Row gutter={[16, 16]}>
        {/* Top Events */}
        <Col xs={24} lg={isRootUser ? 12 : 24}>
          <Card
            title={
              <div>
                <Text strong>Top Performing Events</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Ranked by revenue
                </Text>
              </div>
            }
          >
            <List
              dataSource={ordersData.top_events ?? []}
              locale={{
                emptyText: <Text type="secondary">No event data yet.</Text>,
              }}
              renderItem={(evt, i) => {
                const maxRevenue = ordersData.top_events?.[0]?.revenue || 1;
                return (
                  <List.Item>
                    <div style={{ width: "100%" }}>
                      <Row justify="space-between" align="middle">
                        <Col>
                          <Space>
                            <Tag
                              color={
                                i === 0
                                  ? "gold"
                                  : i === 1
                                    ? "default"
                                    : "orange"
                              }
                              style={{ fontWeight: 700 }}
                            >
                              #{i + 1}
                            </Tag>
                            <div>
                              <Text strong style={{ fontSize: 13 }}>
                                {evt.title ?? evt.entity_id}
                              </Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                {evt.tickets_sold} tickets sold
                              </Text>
                            </div>
                          </Space>
                        </Col>
                        <Col>
                          <Text strong>₹{evt.revenue?.toLocaleString()}</Text>
                        </Col>
                      </Row>
                      <div
                        style={{
                          height: 6,
                          borderRadius: 3,
                          background: "#f0f0f0",
                          marginTop: 8,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            borderRadius: 3,
                            background:
                              "linear-gradient(90deg, #6366f1, #818cf8)",
                            width: `${(evt.revenue / maxRevenue) * 100}%`,
                            transition: "width 0.3s",
                          }}
                        />
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>

        {/* Member Growth */}
        {isRootUser && (
          <Col xs={24} lg={12}>
            <Card
              title={
                <div>
                  <Text strong>Member Growth</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Memberships started vs ended (cancelled or expired) per
                    month
                  </Text>
                </div>
              }
            >
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={memberTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Bar
                    dataKey="newMembers"
                    name="Started"
                    fill="#6366f1"
                    radius={[6, 6, 0, 0]}
                  />
                  <Bar
                    dataKey="churned"
                    name="Ended"
                    fill="#f43f5e"
                    radius={[6, 6, 0, 0]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        )}
      </Row>

      {/* Additional Metrics */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Monthly Recurring Revenue"
              value={membersData.mrr}
              prefix="₹"
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Annual Recurring Revenue"
              value={membersData.arr}
              prefix="₹"
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Churn Rate (last 30 days)"
              value={membersData.churn_rate_30d_pct}
              suffix="%"
              precision={1}
              valueStyle={{
                color:
                  (membersData.churn_rate_30d_pct ?? 0) > 5
                    ? "#ff4d4f"
                    : "#52c41a",
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Analytics;
